import React from 'react';
import { Plane, Code, Users, Target, Award, Globe, Heart } from 'lucide-react';
import { FaPlane, FaLaptopCode } from 'react-icons/fa';
import { useLanguage } from '@/contexts/LanguageContext';
import { Link } from 'react-router-dom';

const About = () => {
  const { language } = useLanguage();

  return (
    <div className="min-h-screen bg-white" data-testid="about-page">
      {/* Hero Section - Couleurs maquette */}
      <section className="relative py-20 bg-gradient-to-r from-maquette-blue to-maquette-dark text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }}></div>
        </div>
        
        <div className="container mx-auto px-4 lg:px-8 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              {language === 'fr' && 'À Propos de Nous'}
              {language === 'en' && 'About Us'}
              {language === 'ar' && 'من نحن'}
            </h1>
            <p className="text-xl md:text-2xl text-gray-100">
              {language === 'fr' && 'HORIZONS CONNECTÉS - Votre partenaire de confiance pour vos voyages et votre transformation digitale'}
              {language === 'en' && 'HORIZONS CONNECTÉS - Your trusted partner for travel and digital transformation'}
              {language === 'ar' && 'HORIZONS CONNECTÉS - شريكك الموثوق للسفر والتحول الرقمي'}
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-maquette-text mb-6">
                {language === 'fr' && 'Deux expertises, une vision'}
                {language === 'en' && 'Two expertises, one vision'}
                {language === 'ar' && 'خبرتان، رؤية واحدة'}
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-12">
              {/* Agence de Voyage */}
              <div className="bg-white p-8 rounded-2xl shadow-xl border-t-4 border-maquette-blue">
                <div className="flex justify-center mb-6">
                  <div className="bg-maquette-blue/10 rounded-full p-6">
                    <FaPlane className="text-maquette-blue text-5xl" />
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-maquette-text mb-4 text-center">
                  {language === 'fr' && '🌍 Agence de Voyage'}
                  {language === 'en' && '🌍 Travel Agency'}
                  {language === 'ar' && '🌍 وكالة السفر'}
                </h3>
                <p className="text-maquette-gray leading-relaxed mb-4">
                  {language === 'fr' && 'Basés à Muscat, Oman, nous sommes spécialisés dans l\'organisation de voyages sur mesure pour les particuliers et les entreprises. Que ce soit pour des circuits touristiques, des voyages d\'affaires ou du team-building international, nous créons des expériences inoubliables.'}
                  {language === 'en' && 'Based in Muscat, Oman, we specialize in organizing customized trips for individuals and businesses. Whether for tourist circuits, business travel or international team-building, we create unforgettable experiences.'}
                  {language === 'ar' && 'مقرنا في مسقط، عمان، نحن متخصصون في تنظيم الرحلات المخصصة للأفراد والشركات. سواء للدوائر السياحية أو السفر التجاري أو بناء الفريق الدولي، نحن نخلق تجارب لا تنسى.'}
                </p>
                <ul className="space-y-2 text-maquette-gray">
                  <li className="flex items-center space-x-2">
                    <span className="text-maquette-blue">✓</span>
                    <span>
                      {language === 'fr' && 'Circuits personnalisés'}
                      {language === 'en' && 'Customized tours'}
                      {language === 'ar' && 'جولات مخصصة'}
                    </span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <span className="text-maquette-blue">✓</span>
                    <span>
                      {language === 'fr' && 'Voyages d\'affaires'}
                      {language === 'en' && 'Business travel'}
                      {language === 'ar' && 'السفر التجاري'}
                    </span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <span className="text-maquette-blue">✓</span>
                    <span>
                      {language === 'fr' && 'Team-building international'}
                      {language === 'en' && 'International team-building'}
                      {language === 'ar' && 'بناء فريق دولي'}
                    </span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <span className="text-maquette-blue">✓</span>
                    <span>
                      {language === 'fr' && 'Destinations mondiales'}
                      {language === 'en' && 'Global destinations'}
                      {language === 'ar' && 'وجهات عالمية'}
                    </span>
                  </li>
                </ul>
              </div>

              {/* Société IT */}
              <div className="bg-white p-8 rounded-2xl shadow-xl border-t-4 border-maquette-blue">
                <div className="flex justify-center mb-6">
                  <div className="bg-maquette-blue/10 rounded-full p-6">
                    <FaLaptopCode className="text-maquette-blue text-5xl" />
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-maquette-text mb-4 text-center">
                  {language === 'fr' && '💻 Société IT'}
                  {language === 'en' && '💻 IT Company'}
                  {language === 'ar' && '💻 شركة تقنية المعلومات'}
                </h3>
                <p className="text-maquette-gray leading-relaxed mb-4">
                  {language === 'fr' && 'Notre branche technologique accompagne les entreprises dans leur transformation digitale. Du développement d\'applications web et mobile au cloud computing, nous offrons des solutions innovantes adaptées aux besoins locaux et internationaux.'}
                  {language === 'en' && 'Our technology branch supports companies in their digital transformation. From web and mobile application development to cloud computing, we offer innovative solutions adapted to local and international needs.'}
                  {language === 'ar' && 'يدعم فرعنا التكنولوجي الشركات في تحولها الرقمي. من تطوير تطبيقات الويب والهاتف المحمول إلى الحوسبة السحابية، نقدم حلولاً مبتكرة تتكيف مع الاحتياجات المحلية والدولية.'}
                </p>
                <ul className="space-y-2 text-maquette-gray">
                  <li className="flex items-center space-x-2">
                    <span className="text-maquette-blue">✓</span>
                    <span>
                      {language === 'fr' && 'Développement web & mobile'}
                      {language === 'en' && 'Web & mobile development'}
                      {language === 'ar' && 'تطوير الويب والجوال'}
                    </span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <span className="text-maquette-blue">✓</span>
                    <span>
                      {language === 'fr' && 'Solutions cloud'}
                      {language === 'en' && 'Cloud solutions'}
                      {language === 'ar' && 'حلول سحابية'}
                    </span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <span className="text-maquette-blue">✓</span>
                    <span>
                      {language === 'fr' && 'Transformation digitale'}
                      {language === 'en' && 'Digital transformation'}
                      {language === 'ar' && 'التحول الرقمي'}
                    </span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <span className="text-maquette-blue">✓</span>
                    <span>
                      {language === 'fr' && 'Support technique 24/7'}
                      {language === 'en' && 'Technical support 24/7'}
                      {language === 'ar' && 'دعم فني 24/7'}
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-maquette-text mb-4">
                {language === 'fr' && 'Nos Valeurs'}
                {language === 'en' && 'Our Values'}
                {language === 'ar' && 'قيمنا'}
              </h2>
            </div>

            <div className="grid md:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="bg-maquette-blue text-white rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <Target size={36} />
                </div>
                <h3 className="text-xl font-bold mb-2 text-maquette-text">
                  {language === 'fr' && 'Excellence'}
                  {language === 'en' && 'Excellence'}
                  {language === 'ar' && 'التميز'}
                </h3>
                <p className="text-maquette-gray">
                  {language === 'fr' && 'Service de qualité supérieure'}
                  {language === 'en' && 'Superior quality service'}
                  {language === 'ar' && 'خدمة عالية الجودة'}
                </p>
              </div>

              <div className="text-center">
                <div className="bg-maquette-blue text-white rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <Award size={36} />
                </div>
                <h3 className="text-xl font-bold mb-2 text-maquette-text">
                  {language === 'fr' && 'Innovation'}
                  {language === 'en' && 'Innovation'}
                  {language === 'ar' && 'الابتكار'}
                </h3>
                <p className="text-maquette-gray">
                  {language === 'fr' && 'Solutions créatives'}
                  {language === 'en' && 'Creative solutions'}
                  {language === 'ar' && 'حلول مبتكرة'}
                </p>
              </div>

              <div className="text-center">
                <div className="bg-maquette-blue text-white rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <Heart size={36} />
                </div>
                <h3 className="text-xl font-bold mb-2 text-maquette-text">
                  {language === 'fr' && 'Passion'}
                  {language === 'en' && 'Passion'}
                  {language === 'ar' && 'الشغف'}
                </h3>
                <p className="text-maquette-gray">
                  {language === 'fr' && 'Dévouement total'}
                  {language === 'en' && 'Total dedication'}
                  {language === 'ar' && 'تفاني كامل'}
                </p>
              </div>

              <div className="text-center">
                <div className="bg-maquette-blue text-white rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <Globe size={36} />
                </div>
                <h3 className="text-xl font-bold mb-2 text-maquette-text">
                  {language === 'fr' && 'Global'}
                  {language === 'en' && 'Global'}
                  {language === 'ar' && 'عالمي'}
                </h3>
                <p className="text-maquette-gray">
                  {language === 'fr' && 'Présence internationale'}
                  {language === 'en' && 'International presence'}
                  {language === 'ar' && 'حضور دولي'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-4 gap-8">
              <div className="text-center p-6 bg-white border-2 border-maquette-blue/20 rounded-xl hover:border-maquette-blue transition-all">
                <div className="text-5xl font-bold text-maquette-blue mb-2">5+</div>
                <div className="text-maquette-gray font-medium">
                  {language === 'fr' && "Années d'expérience"}
                  {language === 'en' && 'Years of experience'}
                  {language === 'ar' && 'سنوات من الخبرة'}
                </div>
              </div>

              <div className="text-center p-6 bg-white border-2 border-maquette-blue/20 rounded-xl hover:border-maquette-blue transition-all">
                <div className="text-5xl font-bold text-maquette-blue mb-2">200+</div>
                <div className="text-maquette-gray font-medium">
                  {language === 'fr' && 'Clients satisfaits'}
                  {language === 'en' && 'Satisfied clients'}
                  {language === 'ar' && 'عميل راضٍ'}
                </div>
              </div>

              <div className="text-center p-6 bg-white border-2 border-maquette-blue/20 rounded-xl hover:border-maquette-blue transition-all">
                <div className="text-5xl font-bold text-maquette-blue mb-2">50+</div>
                <div className="text-maquette-gray font-medium">
                  {language === 'fr' && 'Destinations'}
                  {language === 'en' && 'Destinations'}
                  {language === 'ar' && 'وجهة'}
                </div>
              </div>

              <div className="text-center p-6 bg-white border-2 border-maquette-blue/20 rounded-xl hover:border-maquette-blue transition-all">
                <div className="text-5xl font-bold text-maquette-blue mb-2">24/7</div>
                <div className="text-maquette-gray font-medium">
                  {language === 'fr' && 'Support disponible'}
                  {language === 'en' && 'Available support'}
                  {language === 'ar' && 'دعم متاح'}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - Couleurs maquette */}
      <section className="py-20 bg-gradient-to-r from-maquette-blue to-maquette-dark text-white">
        <div className="container mx-auto px-4 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">
            {language === 'fr' && 'Prêt à travailler avec nous ?'}
            {language === 'en' && 'Ready to work with us?'}
            {language === 'ar' && 'هل أنت مستعد للعمل معنا؟'}
          </h2>
          <p className="text-xl mb-8 text-gray-100 max-w-2xl mx-auto">
            {language === 'fr' && 'Que ce soit pour un voyage inoubliable ou une transformation digitale, nous sommes là pour vous.'}
            {language === 'en' && 'Whether for an unforgettable trip or digital transformation, we are here for you.'}
            {language === 'ar' && 'سواء كانت رحلة لا تُنسى أو تحولًا رقميًا، نحن هنا من أجلك.'}
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center space-x-2 bg-white text-maquette-blue px-8 py-4 rounded-md font-semibold hover:bg-gray-100 transition-all shadow-lg"
          >
            <span>
              {language === 'fr' && 'Contactez-nous'}
              {language === 'en' && 'Contact us'}
              {language === 'ar' && 'اتصل بنا'}
            </span>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default About;
